import { z } from 'zod'
import { TIPI_SCADENZA, statoScadenza, type Ricorrenza, type StatoScadenza, type TipoScadenza } from '@praticheenergia/domain'
import { supabase } from '../lib/supabase'

export const SchemaScadenza = z
  .object({
    impiantoId: z.number().int().positive(),
    tipo: z.enum(TIPI_SCADENZA),
    titolo: z
      .string()
      .trim()
      .transform((v) => (v === '' ? null : v)),
    scadenza: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida'),
    giorniPreavviso: z.number().int().min(0).max(365),
    note: z.string().trim(),
    periodicitaMesi: z.number().int().min(1).max(120).nullable().default(null),
    ricorrenza: z.enum(['da_scadenza', 'da_rinnovo']).nullable().default(null),
    // Euro come testo, virgola o punto: arriva a Postgres come numeric esatto, mai come float.
    importo: z
      .string()
      .trim()
      .default('')
      .refine((v) => v === '' || /^\d+([.,]\d{1,2})?$/.test(v), 'Importo non valido')
      .transform((v) => (v === '' ? null : v.replace(',', '.'))),
  })
  // Stesso vincolo del database (0037): il titolo lo porta solo la personalizzata.
  .transform((d) => ({ ...d, titolo: d.tipo === 'personalizzata' ? d.titolo : null }))
  .refine((d) => d.tipo !== 'personalizzata' || d.titolo !== null, {
    message: 'Una scadenza personalizzata ha bisogno di un titolo',
    path: ['titolo'],
  })
  // Stesso vincolo del database (0049): senza base non si calcola la prossima data.
  .refine((d) => (d.periodicitaMesi === null) === (d.ricorrenza === null), {
    message: 'Indica sia ogni quanto si ripete sia da quando si conta',
    path: ['ricorrenza'],
  })

export type DatiScadenza = z.input<typeof SchemaScadenza>

export interface Scadenza {
  readonly id: number
  readonly impiantoId: number
  readonly tipo: TipoScadenza
  readonly titolo: string | null
  readonly scadenza: string
  readonly giorniPreavviso: number
  readonly completataIl: string | null
  /** Giorno in cui il controllo giornaliero ha mandato l'avviso (0040). */
  readonly avvisataIl: string | null
  readonly note: string
  readonly periodicitaMesi: number | null
  readonly ricorrenza: Ricorrenza | null
  /** Euro come stringa decimale (numeric di Postgres). */
  readonly importo: string | null
  readonly impianto: string
  readonly stato: StatoScadenza
}

/** PostgREST annida la relazione come oggetto, ma i tipi generati la danno
 * come array: si accettano entrambe le forme invece di fidarsi del cast. */
type Annidato<T> = T | T[] | null
const uno = <T,>(v: Annidato<T>): T | null => (Array.isArray(v) ? (v[0] ?? null) : v)

interface RigaScadenza {
  id: number
  impianto_id: number
  tipo: TipoScadenza
  titolo: string | null
  scadenza: string
  giorni_preavviso: number
  completata_il: string | null
  avvisata_il: string | null
  note: string
  periodicita_mesi: number | null
  ricorrenza: Ricorrenza | null
  importo: number | string | null
  impianto: Annidato<{ codice_ditta: string; azienda: Annidato<{ ragione_sociale: string }> }>
}

export async function elencaScadenze(): Promise<Scadenza[]> {
  const { data, error } = await supabase
    .from('scadenza')
    .select('id, impianto_id, tipo, titolo, scadenza, giorni_preavviso, completata_il, avvisata_il, note, periodicita_mesi, ricorrenza, importo, impianto(codice_ditta, azienda(ragione_sociale))')
    .order('scadenza')
  if (error) throw new Error(error.message)
  return ((data ?? []) as unknown as RigaScadenza[]).map((r) => {
    const impianto = uno(r.impianto)
    const azienda = uno(impianto?.azienda ?? null)
    const calcolabile = {
      scadenza: r.scadenza,
      giorniPreavviso: r.giorni_preavviso,
      completataIl: r.completata_il,
    }
    return {
      id: r.id,
      impiantoId: r.impianto_id,
      tipo: r.tipo,
      titolo: r.titolo,
      ...calcolabile,
      avvisataIl: r.avvisata_il,
      note: r.note,
      periodicitaMesi: r.periodicita_mesi,
      ricorrenza: r.ricorrenza,
      importo: r.importo === null ? null : String(r.importo),
      impianto: [azienda?.ragione_sociale, impianto && `(${impianto.codice_ditta})`].filter(Boolean).join(' '),
      stato: statoScadenza(calcolabile),
    }
  })
}

function errore(e: { code?: string; message: string }): Error {
  return new Error(e.code === '23505' ? 'Scadenza gia presente per questo impianto e questa data' : e.message)
}

/** `id` nullo = nuova scadenza. */
export async function salvaScadenza(id: number | null, dati: DatiScadenza): Promise<void> {
  const d = SchemaScadenza.parse(dati)
  const riga = {
    impianto_id: d.impiantoId,
    tipo: d.tipo,
    titolo: d.titolo,
    scadenza: d.scadenza,
    giorni_preavviso: d.giorniPreavviso,
    note: d.note,
    periodicita_mesi: d.periodicitaMesi,
    ricorrenza: d.ricorrenza,
    importo: d.importo,
  }
  const { error } =
    id === null
      ? await supabase.from('scadenza').insert(riga)
      : await supabase.from('scadenza').update(riga).eq('id', id)
  if (error) throw errore(error)
}

/**
 * Con una data la segna come fatta e, se si ripete, il database apre la successiva nella
 * stessa transazione (0049). `data` nulla riapre una scadenza segnata per sbaglio: la
 * successiva gia' creata resta, si elimina a mano se serve.
 */
export async function completaScadenza(id: number, data: string | null): Promise<void> {
  const { error } =
    data === null
      ? await supabase.from('scadenza').update({ completata_il: null }).eq('id', id)
      : await supabase.rpc('completa_scadenza', { p_id: id, p_data: data })
  if (error) throw errore(error)
}

export async function eliminaScadenza(id: number): Promise<void> {
  const { error } = await supabase.from('scadenza').delete().eq('id', id)
  if (error) throw errore(error)
}
